/* Ссылка, промокод, проценты и опубликованные условия приглашения из GET /app/partner. */
import { endpoints } from '../api';
import { storyBackgrounds, type PartnerLevel, type PartnerOverview } from '../api/partner';
import type { StoryMaterial, StoryTexts } from '../api/rewards';
import type { Me } from '../api/types';

type RefState = {
  link: string;
  code: string;
  pct: number;
  max: number;
  months: number;
  giftKnown: boolean;
  gift: { bonus: number; bonusDays: number; disc: number; discDays: number };
  at: number | null;
  sub: number | null;
  pack: boolean;
  setVia(v: string): void;
  setBought(v: boolean): void;
};

/** Уровни по порядку порога покупателей; без списка — текущий и следующий */
export function partnerLevels(p: Pick<PartnerOverview, 'levels' | 'level' | 'next'>): PartnerLevel[] {
  const list = p.levels?.length ? p.levels : [p.level, p.next].filter((x): x is PartnerLevel => !!x);
  return [...list].sort((a, b) => a.buyers - b.buyers);
}

/** Плательщик для «≈ денег» вне листа вывода: в базовой валюте пользователя, иначе первый */
export function payoutPayee(payees: PartnerOverview['withdraw']['payees'], cur: string | null | undefined) {
  return payees.find(x => !!cur && x.currency.toLowerCase() === cur.toLowerCase()) ?? payees[0];
}

export function applyPartnerRef(p: PartnerOverview) {
  const C = window.BotCore;
  const REF = C.REF as unknown as RefState;
  const levels = partnerLevels(p);
  REF.link = p.link.replace(/^https?:\/\//i, '');
  REF.code = p.code;
  if (levels.length) {
    REF.pct = levels[0].pct;
    REF.max = Math.max(...levels.map(x => x.pct));
  }
  if (p.months > 0) REF.months = p.months;
  REF.giftKnown = !!p.referral_gift;
  if (p.referral_gift)
    REF.gift = {
      bonus: p.referral_gift.bonus,
      bonusDays: p.referral_gift.burn_days,
      disc: p.referral_gift.discount_pct,
      discDays: p.referral_gift.discount_days,
    };
  /* «≈ деньги» везде считаются по плательщику сервера; плательщиков несколько — по тому, кто платит в базовой валюте пользователя
     (как у шаблона: «≈ 18 000 ₽ в месяц»), иначе по первому; точная сумма — в листе вывода по выбранному статусу */
  const payee = payoutPayee(p.withdraw.payees, (C.prefs as { get?(): { cur?: string } } | undefined)?.get?.().cur);
  C.coin.terms({ min: p.withdraw.terms?.min, days: p.withdraw.terms?.days, cur: payee?.currency.toLowerCase(), rate: payee?.rate });
  C.coin.set(p.withdraw.withdrawable);
}

/** Personal benefit comes from the pinned successful bind receipt, never a local credit. */
export function applyReferralState(me: Me) {
  if (!me.referral) return;
  const ref = window.BotCore.REF as unknown as RefState,
    r = me.referral,
    g = r.gift;
  if (g && r.since) {
    ref.giftKnown = true;
    ref.gift = {
      bonus: g.bonus,
      bonusDays: Math.round((Date.parse(g.bonus_expires_at) - Date.parse(r.since)) / 864e5),
      disc: g.discount_pct,
      discDays: g.discount_days,
    };
  } else if (ref.giftKnown === undefined) ref.giftKnown = false;
  ref.setVia(r.via);
  ref.setBought(r.bought);
  ref.at = g && r.since ? Date.parse(r.since) : null;
  ref.sub = g?.discount_ends_at ? Date.parse(g.discount_ends_at) - g.discount_days * 864e5 : null;
  ref.pack = g?.discount_state === 'used';
}

/** Картинка сторис со ссылкой и промокодом — с сервера (тексты на ней те же, что на карточке, на языке профиля);
    адрес только https без учётных данных, без share_proof задание не засчитать */
async function storyMaterial(tpl: string, _caption: string, texts: StoryTexts): Promise<StoryMaterial> {
  const m = await endpoints.rewards.storyMaterial(tpl, texts);
  const u = new URL(m.media_url);
  if (u.protocol !== 'https:' || u.username || u.password || !m.share_proof) throw new Error('story_material_invalid');
  return m;
}

let catalog: Promise<void> | null = null;

/** Общий лист сторис ядра (BotCore.story) берёт материал у сервера и показывает только шаблоны его каталога — на их обложках */
export function serveStories() {
  const story = window.BotCore.story;
  story.serve(storyMaterial);
  catalog ??= endpoints.partner
    .materials()
    .then(c => {
      story.only(c.items.filter(x => x.kind === 'story' && x.available).map(x => x.id));
      story.bg(storyBackgrounds(c));
    })
    .catch(() => {
      story.only([]);
      story.bg(null);
      catalog = null;
    });
}
